import emailAuditLogStyles from './email-audit-log.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type {
  EmailAuditFilterHints,
  EmailAuditLogDto,
  DataTableFilterDescriptor as ApiFilterDescriptor,
} from '../../api/agwa-api-model';
import { ApiEmailAudit } from '../../api/core/api-email-audit';
import { keptTextPlain, renderKeptText } from '../../language/kept-text';
import { personIdsIn } from '../../language/person-tag';
import { userDirectoryInstance } from '../../global-data/user-directory-instance';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { emailTypeLabel, sourceModuleLabel } from './email-audit-labels';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '../../components/search/agwa-search';
import {
  isSelectionEmpty,
  selectedValues,
  type FilterFacet,
  type FilterSelection,
} from '../../components/filter/filter-facets';
import { SEARCH_CLAUSE_KEY } from '../../components/filter/agwa-filter-summary';
import '../../components/filter/agwa-filter-rail';
import '../../components/filter/agwa-filter-summary';
import '../../components/layout/agwa-fullscreen-block';
import '../../components/empty-state/agwa-empty-state';
import '../../components/data-table/data-table';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';

import type {
  DataTableColumnDef,
  DataTable,
} from '../../components/data-table/data-table';
import type {
  DataTableDataProvider,
  DataTableFilterDescriptor,
} from '../../components/data-table/data-table-provider';

/** Facet keys are the server's filter field names, so a tick maps straight to a descriptor. */
const FACET_EMAIL_TYPE = 'emailType';
const FACET_SOURCE_MODULE = 'sourceModule';

/**
 * Email Audit Log viewer component.
 * Displays email audit logs with filtering, sorting, and pagination.
 * Only accessible to AGWA staff with MemberAdmin rights.
 */
@customElement('email-audit-log')
export class EmailAuditLog extends WithUserProfileConsumer(LitElement) {
  @state() private auditProvider?: DataTableDataProvider<EmailAuditLogDto>;
  @state() private error?: string;
  @state() private hasItems = false;
  @state() private hasFetched = false;

  /**
   * The rail's ticks, keyed by facet; empty means nothing is narrowing. Each facet is sent
   * as one `eq` descriptor per ticked value, which the job reads as alternatives.
   */
  @state() private filterSelection: FilterSelection = {};
  @state() private searchText = '';
  /** The matched count the last first-page fetch reported, for the sentence above the rows. */
  @state() private matchedCount: number | null = null;

  /**
   * The values the facets offer, from the server: every email type and source module any
   * row holds. A hard-coded list offered a type nothing writes and could not name four that
   * the rows carry (2026-09-06); the rail lists what exists. Arrives with the first page and
   * is kept through load-more.
   */
  @state() private filterHints: EmailAuditFilterHints | null = null;

  static styles = [sharedStyles, unsafeCSS(emailAuditLogStyles)];

  private get auditColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Date Sent',
        fieldName: 'sentUtc',
        width: '180px',
        sortable: true,
        fixed: 'left',
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          item.sentUtc
            ? html`<sl-format-date
                date=${item.sentUtc}
                month="short"
                day="numeric"
                year="numeric"
                hour="2-digit"
                minute="2-digit"
              ></sl-format-date>`
            : html``,
      },
      {
        title: 'Email Type',
        fieldName: 'emailType',
        width: '160px',
        sortable: true,
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          this.renderEmailTypeBadge(item.emailType),
      },
      {
        title: 'Subject',
        fieldName: 'subject',
        width: '300px',
        sortable: true,
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          html`<span class="subject-cell" title=${item.subject}
            >${item.subject}</span
          >`,
      },
      {
        title: 'Recipients',
        fieldName: 'recipients',
        width: '250px',
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          html`<span class="recipients-cell" title=${item.recipients}
            >${item.recipients}</span
          >`,
      },
      {
        title: 'Reason',
        fieldName: 'reasonCode',
        width: '140px',
        sortable: true,
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          item.reasonCode ? this.renderReasonBadge(item.reasonCode) : html`—`,
      },
      {
        title: 'Source Module',
        fieldName: 'sourceModule',
        width: '160px',
        sortable: true,
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          html`${item.sourceModule ? sourceModuleLabel(item.sourceModule) : '—'}`,
      },
      {
        title: 'Details',
        fieldName: 'reasonDescription',
        width: '200px',
        // Kept text (CONTACT-010): a person the server named is a reference, drawn as their name
        // with their card, and in the title as their plain display name; a term tag as its word.
        displayValue: (_table: DataTable, item: EmailAuditLogDto) =>
          item.reasonDescription
            ? html`<span
                class="reason-cell"
                title=${keptTextPlain(item.reasonDescription)}
                >${renderKeptText(item.reasonDescription)}</span
              >`
            : html`—`,
      },
    ];
  }

  private renderEmailTypeBadge(emailType: string) {
    const variantMap: Record<string, string> = {
      CertificateExpiry: 'warning',
      PasswordReset: 'primary',
      UserInvitation: 'success',
      Notification: 'neutral',
    };
    const variant = variantMap[emailType] ?? 'neutral';
    return html`<sl-badge variant=${variant} pill
      >${emailTypeLabel(emailType)}</sl-badge
    >`;
  }

  private renderReasonBadge(reasonCode: string) {
    const variantMap: Record<string, string> = {
      Expired: 'danger',
      ExpiringSoon: 'warning',
      Upcoming: 'primary',
    };
    const variant = variantMap[reasonCode] ?? 'neutral';
    return html`<sl-badge variant=${variant} pill>${reasonCode}</sl-badge>`;
  }

  private createAuditProvider() {
    this.auditProvider = async (request) => {
      this.error = undefined;

      try {
        // One descriptor per ticked value; the job treats a repeated field as any-of.
        const filters: DataTableFilterDescriptor<string>[] = [];
        for (const facet of [FACET_EMAIL_TYPE, FACET_SOURCE_MODULE]) {
          for (const value of selectedValues(this.filterSelection, facet)) {
            filters.push({ field: facet, operator: 'eq', value });
          }
        }

        const api = ApiFactory.authenticated(ApiEmailAudit);
        const res = await api.searchEmailAuditLogs({
          sliceStart: request.sliceStart ?? 0,
          sliceLength: request.sliceLength ?? 100,
          filters: filters as unknown as ApiFilterDescriptor[],
          freeText: this.searchText || undefined,
          // The table states the order; see the security audit log's provider for why no copy of
          // it lives here any more.
          sort: request.sort.map((s) => ({
            field: s.field,
            direction: s.direction === 'desc' ? 'Desc' : 'Asc',
          })),
          requiresCount: request.sliceStart === 0,
        });

        if (!res.success) {
          throw new Error(res.error || 'Failed to load email audit logs');
        }

        const items = res.data?.items ?? [];
        // The people the details name (CONTACT-010), looked up before the rows render: the cell's
        // title is plain text fixed when the cell renders, and only <user-name> follows the
        // directory after. A lookup that fails costs only the title's name, never the page.
        await userDirectoryInstance()
          .preFetch(
            items.flatMap((item) => personIdsIn(item.reasonDescription ?? ''))
          )
          .catch(() => undefined);
        this.hasItems = items.length > 0 || (request.sliceStart ?? 0) > 0;
        this.hasFetched = true;
        if ((request.sliceStart ?? 0) === 0) {
          this.matchedCount = res.data?.totalCount ?? null;
        }
        if (res.data?.filterHints) {
          this.filterHints = res.data.filterHints;
        }

        return {
          items,
          totalCount: res.data?.totalCount,
          hasMore: res.data?.hasMore ?? false,
        };
      } catch (e) {
        this.hasFetched = true;
        this.error = (e as Error).message || 'Failed to load email audit logs';
        throw e;
      }
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.createAuditProvider();
  }

  /**
   * A new provider makes the table refetch from the first page with the current query.
   *
   * The fetched flags are reset FIRST, because an empty result swaps the table for the
   * empty state, and a table that is not on the page cannot refetch: ticking a category
   * with no rows and unticking it again left "0 entries" on screen for good (architect,
   * 2026-09-06, the Role category). Resetting puts the table back.
   *
   * The table fetches by itself, once: a table already on the page reloads when its provider
   * changes, and a table put back fetches when it first renders (data-table.ts `updated` and
   * `firstUpdated`). Asking it to fetch as well sent every search and filter change to the server
   * twice (#398 C15 d, as the security audit log's C14 e).
   */
  private applyFilters() {
    this.hasFetched = false;
    this.hasItems = false;
    this.matchedCount = null;
    this.createAuditProvider();
  }

  private handleFilterChange(e: CustomEvent<{ selection: FilterSelection }>) {
    this.filterSelection = e.detail.selection;
    this.applyFilters();
  }

  /** Everything narrowing the list is undone at once - the ticks AND the search box. */
  private handleFilterClear() {
    this.filterSelection = {};
    this.searchText = '';
    this.applyFilters();
  }

  private handleFilterDrop(e: CustomEvent<{ key: string }>) {
    if (e.detail.key === SEARCH_CLAUSE_KEY) {
      this.searchText = '';
    } else {
      this.filterSelection = { ...this.filterSelection, [e.detail.key]: [] };
    }
    this.applyFilters();
  }

  /**
   * Runs the search the box reports, once. The box reports one search twice - `search-input` as the
   * person types and `search-change` when they press Enter or clear - so a value already searched is
   * not searched again (#398 C15 d). A clear is reported as `search-clear` and then a change to
   * nothing, so the change finds the box already empty and is the one dropped.
   */
  private handleSearchInput(e: CustomEvent<{ value: string }>) {
    if (e.detail.value === this.searchText) return;
    this.searchText = e.detail.value;
    this.applyFilters();
  }

  private handleSearchClear() {
    this.searchText = '';
    this.applyFilters();
  }

  private get anyFilterApplied(): boolean {
    return (
      this.searchText.trim() !== '' || !isSelectionEmpty(this.filterSelection)
    );
  }

  /**
   * The facets, valued from the server's hints so they name exactly what the rows hold.
   * Labels come from emailTypeLabel and sourceModuleLabel, the same words each row shows: a code
   * naming a domain concept reads through its term, any other code spaced out or as written.
   */
  private get filterFacets(): FilterFacet[] {
    return [
      {
        key: FACET_EMAIL_TYPE,
        title: 'Email type',
        phrase: 'of type',
        options: (this.filterHints?.emailTypes ?? []).map((code) => ({
          value: code,
          label: emailTypeLabel(code),
        })),
        emptyHint: 'No emails have been sent yet.',
      },
      {
        key: FACET_SOURCE_MODULE,
        title: 'Source module',
        phrase: 'sent by',
        options: (this.filterHints?.sourceModules ?? []).map((code) => ({
          value: code,
          label: sourceModuleLabel(code),
        })),
        emptyHint: 'No emails have been sent yet.',
      },
    ];
  }

  render() {
    const showEmpty = this.hasFetched && !this.hasItems && !this.error;

    // No page title: the app header already carries it (architect, 2026-09-06). The search
    // sits alone above; everything that narrows rows is in the rail beside the table.
    return html`
      <div class="audit-page">
        <!--
          The search row and the list are ONE block that can take the whole viewport in place
          (D-106): searching while scanning the rows is the common case, so the row travels with
          the rail and the table rather than being left behind on a page nobody can see.
        -->
        <agwa-fullscreen-block .title=${'Email History'}>
          <div class="filters" slot="header">
            <agwa-search
              size="small"
              .value=${this.searchText}
              placeholder="Search subject or recipients..."
              @search-input=${this.handleSearchInput}
              @search-change=${this.handleSearchInput}
              @search-clear=${this.handleSearchClear}
            ></agwa-search>
            ${this.error ? html`<span class="error">${this.error}</span>` : nothing}
          </div>
          <div class="list-body">
            <agwa-filter-rail
              storage-key="email-audit-log"
              .facets=${this.filterFacets}
              .selection=${this.filterSelection}
              ?can-clear=${this.anyFilterApplied}
              @filter-change=${this.handleFilterChange}
              @filter-clear=${this.handleFilterClear}
            ></agwa-filter-rail>
            <div class="list-main">
              <agwa-filter-summary
                .facets=${this.filterFacets}
                .selection=${this.filterSelection}
                search-text=${this.searchText}
                subject="emails"
                .shownCount=${this.matchedCount ?? 0}
                .totalCount=${null}
                @filter-drop=${this.handleFilterDrop}
              ></agwa-filter-summary>
              ${
                showEmpty
                  ? html`<agwa-empty-state
                      icon="envelope"
                      title="No email history"
                      message="No email audit logs found matching your criteria."
                    ></agwa-empty-state>`
                  : this.auditProvider
                    ? // NEWEST FIRST. A person opens an email log to see what just went out, and
                      // the log only ever grows, so opening on the oldest row means scrolling past
                      // the whole history to reach the reason they came. The property is
                      // `sortAscending`, and the table reads undefined as ASCENDING, so the
                      // descending order is stated rather than left to a default. It replaces a
                      // `.sortDirection` binding that named no property of the table and did
                      // nothing, which is why this log opened on the oldest entry.
                      html`<agwa-lit-table
                        fill-height
                        .columns=${this.auditColumns}
                        .dataProvider=${this.auditProvider}
                        .pageLength=${100}
                        .showLoadMore=${true}
                        sortField="sentUtc"
                        .sortAscending=${false}
                      ></agwa-lit-table>`
                    : html``
              }
            </div>
          </div>
        </agwa-fullscreen-block>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'email-audit-log': EmailAuditLog;
  }
}
